define((require) => {
    "use strict";

    const _               = require("underscore");
    const AssociatedModel = require("backbone.associations");
    const Promise         = require("shared/util/Promise");
    const {Logger}        = require("shared/util/Logger");

    /**
     * Extends Backbone model with the following features:
     * <ul>
     *     <li>
     *         <p>The "computed" property can be used to set up read-only computed properties. It's a map of property
     *         names to objects with the following parameters:</p>
     *         <ul>
     *             <li><b>dep</b>: The list of properties the computed value depends on. The value will be recomputed
     *                 whenever any of these values change.</li>
     *             <li><b>value</b>: The function that computes the value. Takes the values of the properties defined
     *                 in <b>dep</b> as parameters.</li>
     *             <li><b>computeDefault</b>: If true, the initial value of the property will be computed by invoking
     *                 the <b>value</b> function shortly after the model is constructed.</li>
     *             <li><b>resolvePromises</b>: If true, when the value function returns a promise, the computed
     *                 property will be set to the value of the promise when it resolves. Otherwise, the property will
     *                 be set to the promise itself.</li>
     *         </ul>
     *         <p>All properties are optional, but if <b>dep</b> or <b>computeDefault</b> are defined, <b>value</b> is
     *         required.</p>
     *         <p>If you define a computed property without configuring dependencies, it will behave like a normal
     *         property, but will not be included when the model is saved. So this is useful for defining transient
     *         properties.</p>
     *         <p>Set a default value for a computed property by setting it in the normal default object for the model.</p>
     *     </li>
     *     <li>
     *         Models will not be automatically updated when saved to the server. This ensures that change events will
     *         not be triggered as the result of a save.
     *     </li>
     *     <li>
     *         The "fetchNewUrl" property, if defined, will be used when fetching new models. This allows you to use a
     *         different URL for initial fetches.
     *     </li>
     *     <li>
     *         Static findById, findByQuery, and findOrCreate methods for looking up models across caching layers.
     *     </li>
     * </ul>
     *
     * @author Bo Gotthardt
     * @class ImprovedModel
     * @constructor
     */
    return AssociatedModel.extend("ImprovedModel", {

        constructor() {
            // Computed attributes.
            if (this.computed) {
                // Factory method that returns a new listener function bound to the property name and configuration,
                // but supplied the options given to the "set(..)" call that triggered this computation as well...
                const listener = (name, config) => {
                    const instanceScope = this;
                    return function(...args) {
                        instanceScope._computeProperty(name, config, args[args.length - 1]); // options always last on change events
                    };
                };

                _.each(this.computed, (config, name) => {
                    const callback = listener(name, config);
                    // Listen for changes for all the required attributes.
                    if (config.deps) {
                        _.each(config.deps, (dep) => {
                            this.listenTo(
                                this,
                                `change:${dep} add:${dep} remove:${dep} nested-change:${dep}`,
                                callback
                            );
                        });
                    }

                    if (config.computeDefault) {
                        _.defer(callback);
                    }
                });
            }

            // Now call super constructor, only after we have computed the properties, since the computed
            // properties are managed by Backbone...
            this.__super();
        },

        _computeProperty(propertyName, config, options) {
            // Get each of the required attributes to use as parameters.
            const parameters = config.deps.map((dep) => this.get(dep));
            // Add "options", if any, from the set that trigger this computing as the last argument...
            parameters.push(options);

            // Compute the value, but do so silently if it depends on it self, e.g. when a property name is actually
            // an identifier for a enum constant to be resolved. However, this will fire the first change event, e.g.
            // by name, not the second time around, but the use-case for self-dependencies is currently limited to
            // "enum types", so we should be good. In addition, "updateOnSync" can specify "silent", then trigger
            // a non-silent change here, which we must also handle...
            const silent = typeof config.silent === "undefined" ? config.deps.indexOf(propertyName) !== -1 : config.silent;
            const val = config.value.apply(this, parameters);
            const opts = _.extend({computedProperty: true, silent}, options);

            // For promises, wait for the resolved value
            if (config.resolvePromises && Promise.isPromise(val)) {
                val.done((value) => {
                    this.set(propertyName, value, opts);
                });

            // Otherwise just set the value
            } else {
                this.set(propertyName, val, opts);
            }
        },

        /**
         * Returns a user-friendly distinguished name for this model instance, typically used to report errors.
         * Uses `this._distinguishedName()` to find a name, or if not found uses the model `__typeName` property, or falls back to `"resource"`.
         * Then applies Id and version information, depending on the log level.
         *
         * @return {String} The distinguished name; never null, but can be empty if not applicable for this model type.
         */
        distinguishedName() { // override _distinguishedName() in sub-classes, not this method!
            const id = this.get("id");
            let name = this._distinguishedName();
            let version;
            let i;

            // Default name?
            if (!name) {
                name = this.__typeName || "resource";
            }

            // Verbose name for easier debugging? Note that not all models may have an id, or it may not have been
            // set yet...
            if (Logger.level() >= Logger.LOG && id) {
                // We may already have debug information in the name from sub-type, so append to it...
                if (name.charAt(name.length - 1) === "]") {
                    name = `${name.substring(0, name.length - 1)}, `;
                // Add verbose information start marker ([)...
                } else {
                    name += " [";
                }

                i = name.indexOf(id);

                if (i === -1) {
                    name += id;
                }
                // Include version, which will be there if an id *and* if versioned. Can be negative
                // for stale data like old pushed experiences (we do not want to save those by
                // accident, but we need to know the exact (abs) version)...
                if (this.has("version")) {
                    version = Math.abs(this.get("version"));

                    if (i === -1) {
                        name += ", ";
                    }

                    name += `#${version}`;
                }

                name += "]";
            }

            return name;
        },

        /**
         * Sub-class specific hook to override the distinguished name of this model type.
         *
         * @return {String} The distinguished name; can be falsey, which will default to the class name of
         *                  this type, secondarily the string "resource".
         */
        _distinguishedName() {
            return null;
        },

        // Only supported in the editor, and then only where mixed in for non immutable types...
        save() {},

        destroy() {},

        /**
         * A general method to return a valid editedAt, because editedAt will be null at creation,
         * and we want the editedAt to be createdAt in most cases.
         */
        getEditedAt() {
            return this.get("editedAt") || this.get("createdAt");
        }

    }, {

        /** -----------------------------------
         *  Static methods
         *  ---------------------------------- */

        /**
         * Find the model of this type with the specified id.
         *
         * @param   {String} id The model if; cannot be falsey.
         * @returns {Promise} A promise for the model; never falsey.
         */
        findById(id) {
            return this.findByQuery({id});
        },

        /**
         * Find the first model with the same attributes as the query argument.
         *
         * @param   {Object} query The query; cannot be falsey.
         * @returns {Promise} A promise for the model; never falsey.
         */
        findByQuery(query) {
            const Type = this;
            const model = new Type(query);
            // No queuing used in the viewer, hence the resolved result from "fetch()" is raw json, which we do not
            // want - we want the actual model instance, updated with the json...
            return model.fetch().then(() => model);
        }

    });
});



// WEBPACK FOOTER //
// ./src/shared/zbone/ImprovedModel.js